import {
  type PayseraLoginMutationVariables,
  usePayseraLoginMutation,
  usePayseraLoginPollLazyQuery,
} from 'api/core/generated';
import {
  APP_POLLING_INTERVALS,
  AppApiVersions,
  AppProductType,
  AppSearchParams,
  PAYSERA_PAYMENT_STATUSES,
} from 'app-constants';
import { useSearchParams } from 'react-router-dom';
import {
  getAppRoutePathByProductType,
  replaceOrAddQueryParamsInUrl,
} from 'services';

import { useGetProductTypeByUrl } from './use-get-product-type-by-url';

export const useLoginByBanklink = () => {
  const [searchParams] = useSearchParams();
  const productType = useGetProductTypeByUrl();
  const { appRoutePath, productRoutePaths } =
    getAppRoutePathByProductType(productType);

  const [loginViaBanklink, { data, loading, error }] = usePayseraLoginMutation({
    context: { apiVersion: AppApiVersions.core },
  });

  const banklinkLoginRedirectUrlParamsByProductTypeMap = {
    [AppProductType.HIRE_PURCHASE]: {
      [AppSearchParams.referenceKey]:
        searchParams.get(AppSearchParams.referenceKey) || '',
    },
    [AppProductType.SMALL_LOAN]: {
      [AppSearchParams.referenceKey]:
        searchParams.get(AppSearchParams.referenceKey) || '',
    },
    [AppProductType.CREDIT_LINE]: {
      [AppSearchParams.creditAccountHash]:
        searchParams.get(AppSearchParams.creditAccountHash) || '',
    },
    [AppProductType.CREDIT_LIMIT_RECALCULATION]: {
      [AppSearchParams.creditLimitRecalculationHash]:
        searchParams.get(AppSearchParams.creditLimitRecalculationHash) || '',
    },
    [AppProductType.CREDIT_LINE_WITHDRAWAL]: {
      [AppSearchParams.creditLimitRecalculationHash]:
        searchParams.get(AppSearchParams.creditLineWithdrawalHash) || '',
    },
    [AppProductType.INCOME_INSURANCE]: {
      [AppSearchParams.insuranceHash]:
        searchParams.get(AppSearchParams.insuranceHash) || '',
    },
  };

  const banklinkLoginRedirectUrl = replaceOrAddQueryParamsInUrl({
    returnWithBase: true,
    url: `${window.origin}/${appRoutePath}/${productRoutePaths?.LOGIN}`,
    params:
      banklinkLoginRedirectUrlParamsByProductTypeMap[
        productType ?? AppProductType.CREDIT_LINE
      ],
  });

  const banklinkLoginAcceptUrl = replaceOrAddQueryParamsInUrl({
    returnWithBase: true,
    url: banklinkLoginRedirectUrl,
    params: {
      [AppSearchParams.payseraLoginPaymentStatus]:
        PAYSERA_PAYMENT_STATUSES.successful,
    },
  });

  const banklinkLoginCancelUrl = replaceOrAddQueryParamsInUrl({
    returnWithBase: true,
    url: banklinkLoginRedirectUrl,
    params: {
      [AppSearchParams.payseraLoginPaymentStatus]:
        PAYSERA_PAYMENT_STATUSES.failed,
    },
  });

  const [
    startBanklinkLoginStatusPolling,
    {
      data: banklinkLoginStatusData,
      loading: banklinkLoginStatusLoading,
      error: banklinkLoginStatusError,
      stopPolling,
    },
  ] = usePayseraLoginPollLazyQuery({
    context: { apiVersion: AppApiVersions.core },
    pollInterval: APP_POLLING_INTERVALS.default,
  });

  const loginByBankLink = (variables: PayseraLoginMutationVariables) =>
    loginViaBanklink({ variables });

  return {
    loginByBankLink,
    loginByBanklinkProcessing: loading,
    loginByBanklinkError: error,
    loginByBankLinkData: data?.challenge?.redirect_url,
    banklinkLoginStatus: banklinkLoginStatusData?.status,
    banklinkLoginStatusLoading,
    banklinkLoginStatusError,
    startBanklinkLoginStatusPolling,
    stopBanklinkLoginStatusPolling: stopPolling,
    banklinkLoginRedirectUrl,
    banklinkLoginAcceptUrl,
    banklinkLoginCancelUrl,
  };
};
